import { useState } from "react";
import {
  Button,
  Card,
  CardHeader,
  ServiceMark,
} from "@/components/primitives";
import { Icon } from "@/components/Icon";
import { PageHeader } from "@/components/Layout";
import { useSnapshot } from "@/lib/snapshot-context";

export function WebhookInspector() {
  const { data, fmt } = useSnapshot();
  const events = data.webhookEvents;
  const webhookServices = data.services.filter((s) => s.source === "webhook");

  const [selectedId, setSelectedId] = useState<string | undefined>(
    events[0]?.id,
  );
  const selected = events.find((e) => e.id === selectedId);
  const svc = selected
    ? data.services.find((s) => s.id === selected.serviceId)
    : undefined;

  return (
    <>
      <PageHeader
        crumbs={["Status Watch", "Admin", "Webhook inspector"]}
        title="Webhook inspector"
        subtitle={`${webhookServices.length} webhook endpoints active · ${events.length} events in last hour`}
        actions={
          <Button variant="outline" icon="refresh">
            Refresh
          </Button>
        }
      />

      <div
        style={{
          padding: "20px 32px 28px",
          display: "flex",
          flexDirection: "column",
          gap: 16,
        }}
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(2, 1fr)",
            gap: 12,
          }}
        >
          {webhookServices.map((s) => {
            const recent = events.filter((e) => e.serviceId === s.id);
            const last = recent[0];
            const p50 = recent.length
              ? Math.floor(
                  recent.reduce((a, e) => a + e.latency, 0) / recent.length,
                )
              : 0;
            return (
              <Card key={s.id} style={{ padding: 14 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <ServiceMark service={s} size={28} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 600 }}>{s.name}</div>
                    <code
                      style={{
                        fontSize: 11,
                        color: "var(--muted-foreground)",
                        fontFamily: "var(--font-mono)",
                      }}
                    >
                      {s.sourceUrl.replace(/^https?:\/\/[^/]+/, "")}
                    </code>
                  </div>
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 5,
                      fontSize: 11,
                      color: "var(--success)",
                      fontWeight: 600,
                    }}
                  >
                    <span
                      style={{
                        width: 6,
                        height: 6,
                        borderRadius: "50%",
                        background: "var(--success)",
                        boxShadow:
                          "0 0 0 3px color-mix(in oklch, var(--success) 25%, transparent)",
                      }}
                    />
                    Verified
                  </span>
                </div>
                <div
                  style={{
                    display: "flex",
                    gap: 14,
                    marginTop: 12,
                    fontSize: 11,
                    color: "var(--muted-foreground)",
                  }}
                >
                  <span>
                    <b
                      style={{
                        color: "var(--foreground)",
                        fontVariantNumeric: "tabular-nums",
                      }}
                    >
                      {recent.length}
                    </b>{" "}
                    events / hour
                  </span>
                  <span>last: {last ? fmt.rel(last.at) : "—"}</span>
                  <span style={{ marginLeft: "auto" }}>p50 {p50}ms</span>
                </div>
              </Card>
            );
          })}
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1.4fr 1fr",
            gap: 14,
            alignItems: "flex-start",
          }}
        >
          <Card>
            <CardHeader
              title="Recent events"
              description="All inbound webhook POSTs across endpoints"
              right={
                <Button size="sm" variant="ghost" icon="filter">
                  Filter
                </Button>
              }
            />
            <div>
              {events.map((e, i) => {
                const s = data.services.find((s) => s.id === e.serviceId);
                const ok = e.status === 200;
                return (
                  <div
                    role="button"
                    key={e.id}
                    onClick={() => setSelectedId(e.id)}
                    style={{
                      display: "grid",
                      gridTemplateColumns:
                        "22px minmax(0, 1fr) auto 64px 52px",
                      gap: 10,
                      alignItems: "center",
                      padding: "10px 14px",
                      borderTop: i === 0 ? "none" : "1px solid var(--border)",
                      background:
                        selectedId === e.id
                          ? "color-mix(in oklch, var(--primary) 6%, transparent)"
                          : "transparent",
                      cursor: "pointer",
                    }}
                  >
                    {s ? <ServiceMark service={s} size={20} /> : <span />}
                    <code
                      style={{
                        fontSize: 12,
                        fontFamily: "var(--font-mono)",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        color: "var(--foreground)",
                        display: "block",
                        minWidth: 0,
                      }}
                    >
                      {e.path}
                    </code>
                    <span
                      style={{
                        fontSize: 11,
                        color: "var(--muted-foreground)",
                        fontFamily: "var(--font-mono)",
                      }}
                    >
                      {fmt.rel(e.at)}
                    </span>
                    <code
                      style={{
                        fontSize: 11,
                        fontFamily: "var(--font-mono)",
                        color: ok ? "var(--success)" : "var(--destructive)",
                        fontWeight: 600,
                      }}
                    >
                      {e.status} {ok ? "OK" : "FAIL"}
                    </code>
                    <span
                      style={{
                        fontSize: 11,
                        color: "var(--muted-foreground)",
                        textAlign: "right",
                        fontFamily: "var(--font-mono)",
                      }}
                    >
                      {e.latency}ms
                    </span>
                  </div>
                );
              })}
              {events.length === 0 && (
                <div
                  style={{
                    padding: 20,
                    textAlign: "center",
                    fontSize: 12,
                    color: "var(--muted-foreground)",
                  }}
                >
                  No webhook events yet.
                </div>
              )}
            </div>
          </Card>

          <Card>
            <CardHeader
              title="Event detail"
              description={selected?.id ?? "—"}
            />
            <div
              style={{
                padding: 14,
                display: "flex",
                flexDirection: "column",
                gap: 14,
              }}
            >
              {selected && svc ? (
                <>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "auto 1fr",
                      gap: "8px 14px",
                      fontSize: 12,
                    }}
                  >
                    <span style={{ color: "var(--muted-foreground)" }}>
                      Service
                    </span>
                    <span
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 6,
                      }}
                    >
                      <ServiceMark service={svc} size={16} /> {svc.name}
                    </span>
                    <span style={{ color: "var(--muted-foreground)" }}>
                      Endpoint
                    </span>
                    <code
                      style={{
                        fontFamily: "var(--font-mono)",
                        fontSize: 11,
                      }}
                    >
                      {selected.path}
                    </code>
                    <span style={{ color: "var(--muted-foreground)" }}>
                      Received
                    </span>
                    <span
                      style={{
                        fontFamily: "var(--font-mono)",
                        fontSize: 11,
                      }}
                    >
                      {fmt.datetime(selected.at)}
                    </span>
                    <span style={{ color: "var(--muted-foreground)" }}>
                      Response
                    </span>
                    <span
                      style={{
                        fontFamily: "var(--font-mono)",
                        fontSize: 11,
                        color:
                          selected.status === 200
                            ? "var(--success)"
                            : "var(--destructive)",
                      }}
                    >
                      {selected.status} · {selected.latency}ms
                    </span>
                    <span style={{ color: "var(--muted-foreground)" }}>
                      Signature
                    </span>
                    <span>
                      {selected.signature === "ok" ? (
                        <span
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 5,
                            fontSize: 11,
                            color: "var(--success)",
                            fontWeight: 600,
                          }}
                        >
                          <Icon name="shield" size={11} /> HMAC valid
                        </span>
                      ) : (
                        <span
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 5,
                            fontSize: 11,
                            color: "var(--destructive)",
                            fontWeight: 600,
                          }}
                        >
                          <Icon name="x" size={11} /> signature mismatch
                        </span>
                      )}
                    </span>
                  </div>

                  <div>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginBottom: 6,
                      }}
                    >
                      <div
                        style={{
                          fontSize: 11,
                          textTransform: "uppercase",
                          letterSpacing: "0.04em",
                          color: "var(--muted-foreground)",
                          fontWeight: 600,
                        }}
                      >
                        Payload
                      </div>
                      <Button size="sm" variant="ghost" icon="copy">
                        Copy
                      </Button>
                    </div>
                    <pre
                      style={{
                        margin: 0,
                        padding: 12,
                        background: "var(--color-gray-950)",
                        color: "var(--color-gray-100)",
                        borderRadius: 8,
                        fontSize: 11,
                        fontFamily: "var(--font-mono)",
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-all",
                        lineHeight: 1.5,
                        maxHeight: 220,
                        overflow: "auto",
                      }}
                    >
                      {prettyJson(selected.payload)}
                    </pre>
                  </div>
                </>
              ) : (
                <div
                  style={{
                    color: "var(--muted-foreground)",
                    fontSize: 12,
                    textAlign: "center",
                    padding: 20,
                  }}
                >
                  Select an event.
                </div>
              )}
            </div>
          </Card>
        </div>
      </div>
    </>
  );
}

function prettyJson(raw: string): string {
  try {
    return JSON.stringify(JSON.parse(raw), null, 2);
  } catch {
    return raw;
  }
}
